import React, { useCallback, useEffect, useRef, useState } from "react";
import FormBlock from "./FormBlock";
import { Controller, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { ComboboxNamHoc } from "../../../components/combobox-namhoc";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import { crmHocSinhApi } from "../../../api/crmHocSinhApi";
import { trangThaiHocSinhApi } from "../../../api/trangThaiHocSinhApi";
import { AlertIcon, SyncIcon } from "@primer/octicons-react";
import { Box, FormControl, IconButton } from "@primer/react";
import TextInput from "../../../components/ui/text-input";
import DateInput from "../../../components/date-input/DateInput";
import moment from "moment";
import ComboboxGioiTinh from "../../../components/combobox-gioitinh";
import ComboboxDanToc from "../../../components/combobox-dantoc";
import ComboboxTonGiao from "../../../components/combobox-tongiao";
import ComboboxQuocTich from "../../../components/combobox-quoctich";
import { ComboboxTrangThaiHocSinh } from "../../../components/combobox-trangthaihocsinh";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

interface IThongTinHocSinhProps {
  control: any;
  hocSinh: IHocSinh;
  setFormData: React.Dispatch<React.SetStateAction<IHocSinh>>;
  setValue: UseFormSetValue<IHocSinh>;
  generatedCode?: string;
  register: any;
  errors: any;
  formData: IHocSinh;
  trigger: UseFormTrigger<IHocSinh>;
  onReloadMaTn: () => Promise<void>;
  onReloadMaHs: () => Promise<void>;
  isReloadingMaTn: boolean;
  isReloadingMaHs: boolean;
}

interface IDuplicateStudent {
  id: number;
  ho_ten: string;
  ngay_sinh: string | Date | null;
  ma_hs: string;
  ma_tn: string;
  ten_lop: string;
  ten_khoi: string;
  ten_he: string;
  ten_truong: string;
  trang_thai: string;
  nam_hoc: string;
}

const ThongTinHocSinh = (props: IThongTinHocSinhProps) => {
  const {
    control,
    setFormData,
    hocSinh,
    setValue,
    generatedCode,
    register,
    errors,
    formData,
    trigger,
    onReloadMaTn,
    onReloadMaHs,
    isReloadingMaTn,
    isReloadingMaHs,
  } = props;

  const { dm_coso_id } = useCommonSelectedHook();
  const [duplicateStudents, setDuplicateStudents] = useState<IDuplicateStudent[]>([]);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [trangThaiMap, setTrangThaiMap] = useState<Record<number, string>>({});

  // Load danh mục trạng thái học sinh
  useEffect(() => {
    const loadTrangThai = async () => {
      try {
        const res = await trangThaiHocSinhApi.selectAllCheckDangKy(dm_coso_id || 1);
        if (res.is_success && res.data) {
          const map: Record<number, string> = {};
          (res.data as any[]).forEach((item: any) => {
            map[item.id] = item.trang_thai;
          });
          setTrangThaiMap(map);
        }
      } catch { /* ignore */ }
    };
    loadTrangThai();
  }, [dm_coso_id]);

  // Track current ho_ten value (because register doesn't give us reactive value easily)
  const [currentHoTen, setCurrentHoTen] = useState<string>(hocSinh.ho_ten || "");
  const [currentNgaySinh, setCurrentNgaySinh] = useState<Date | null>(
    hocSinh.ngay_sinh ? new Date(hocSinh.ngay_sinh) : null
  );

  // Normalize Vietnamese name for comparison (trim, collapse spaces, lowercase)
  const normalizeName = useCallback((name: string): string => {
    return (name || "").trim().replace(/\s+/g, " ").toLowerCase();
  }, []);

  // Check duplicate when ho_ten or ngay_sinh changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hoTen = currentHoTen?.trim();
    const ngaySinh = currentNgaySinh;

    // Only check if both fields have values
    if (!hoTen || hoTen.length < 2 || !ngaySinh) {
      setDuplicateStudents([]);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      setIsCheckingDuplicate(true);
      try {
        const res = await crmHocSinhApi.checkDuplicate({
          searchText: hoTen,
          dm_coso_id: dm_coso_id || 1,
        });

        if (res.is_success && res.data) {
          const targetNgaySinh = moment(ngaySinh).format("DD/MM/YYYY");
          const normalizedInputName = normalizeName(hoTen);

          const duplicates = (res.data as any[]).filter((student: any) => {
            if (student.id === hocSinh.id && hocSinh.id > 0) return false;

            const studentNgaySinh = student.ngay_sinh
              ? moment(student.ngay_sinh).format("DD/MM/YYYY")
              : "";
            const normalizedStudentName = normalizeName(student.ho_ten || "");

            return (
              normalizedStudentName === normalizedInputName &&
              studentNgaySinh === targetNgaySinh
            );
          });

          // Enrich duplicates with chuyển cấp info (năm học, hệ, khối, lớp mới nhất)
          const enriched = await Promise.all(
            duplicates.map(async (student: any) => {
              try {
                const detail = await crmHocSinhApi.getById(student.id, formData.nam_hoc || "2025 - 2026");
                if (detail.is_success && detail.data) {
                  return {
                    ...student,
                    nam_hoc: detail.data.nam_hoc || detail.data.current_nam_hoc || student.nam_hoc || "",
                    ten_he: detail.data.ten_he || student.ten_he || "",
                    ten_khoi: detail.data.ten_khoi || student.ten_khoi || "",
                    ten_lop: detail.data.ten_lop || student.ten_lop || "",
                    ten_truong: detail.data.ten_truong || student.ten_truong || "",
                    trang_thai: detail.data.trang_thai || trangThaiMap[detail.data.dm_trangthaihocsinh_id] || student.trang_thai || "",
                  };
                }
              } catch {
                // ignore, use original data
              }
              return student;
            })
          );

          setDuplicateStudents(enriched);
        } else {
          setDuplicateStudents([]);
        }
      } catch {
        setDuplicateStudents([]);
      } finally {
        setIsCheckingDuplicate(false);
      }
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentHoTen, currentNgaySinh, hocSinh.id, dm_coso_id, normalizeName]);

  return (
    <FormBlock caption="Thông tin học sinh">
      <Box
        sx={{
          display: "grid",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(5, 1fr)",
          }}
        >
          <Controller
            name="nam_hoc"
            control={control as any}
            rules={{ required: "Vui lòng chọn năm học" }}
            render={({ field }) => (
              <FormControl>
                <FormControl.Label>
                  Năm học <span style={{ color: "red" }}>*</span>
                </FormControl.Label>
                <ComboboxNamHoc
                  value={field.value}
                  onValueChanged={async (value) => {
                    field.onChange(value);
                    setFormData((prev) => ({
                      ...prev,
                      nam_hoc: value,
                    }));

                    if (hocSinh.id > 0) {
                      try {
                        const res = await crmHocSinhApi.getHSCCById(
                          hocSinh.id,
                          value
                        );
                        if (res.is_success && res.data) {
                          setFormData((prev) => ({
                            ...prev,
                            dm_truong_id: res.data.dm_truong_id || 0,
                            dm_khoi_id: res.data.dm_khoi_id || 0,
                            dm_he_id: res.data.dm_he_id || 0,
                            dm_lop_id: res.data.dm_lop_id || 0,
                            dm_trangthaihocsinh_id:
                              res.data.dm_trangthaihocsinh_id || 0,
                          }));

                          const defaultValues = {
                            dm_truong_id: res.data.dm_truong_id || 0,
                            dm_khoi_id: res.data.dm_khoi_id || 0,
                            dm_he_id: res.data.dm_he_id || 0,
                            dm_lop_id: res.data.dm_lop_id || 0,
                            dm_trangthaihocsinh_id:
                              res.data.dm_trangthaihocsinh_id || 0,
                          };

                          Object.entries(defaultValues).forEach(
                            ([key, value]) => {
                              setValue(key as keyof IHocSinh, value);
                            }
                          );
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            dm_truong_id: 0,
                            dm_khoi_id: 0,
                            dm_he_id: 0,
                            dm_lop_id: 0,
                            dm_trangthaihocsinh_id: 0,
                          }));

                          const defaultValues = {
                            dm_truong_id: 0,
                            dm_khoi_id: 0,
                            dm_he_id: 0,
                            dm_lop_id: 0,
                            dm_trangthaihocsinh_id: 0,
                          };

                          Object.entries(defaultValues).forEach(
                            ([key, value]) => {
                              setValue(key as keyof IHocSinh, value);
                            }
                          );
                        }
                      } catch (error) {
                        console.error("Error fetching data:", error);
                      }
                    }
                  }}
                />
              </FormControl>
            )}
          />

            <Controller
              name="ma_tn"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormControl.Label>Mã tiềm năng</FormControl.Label>
                  <Box
                    sx={{
                      position: "relative",
                    }}
                  >
                    <TextInput
                      block
                      {...field}
                      sx={{
                        pr: "36px",
                      }}
                      value={field.value || (hocSinh.id === 0 ? generatedCode : "")}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          ma_tn: e.target.value,
                        }));
                      }}
                      placeholder={hocSinh.id === 0 ? generatedCode || "Đang sinh mã..." : ""}
                    />
                    <IconButton
                      icon={SyncIcon}
                      aria-label="Lấy lại mã tiềm năng mới nhất"
                      variant="invisible"
                      size="small"
                      disabled={isReloadingMaTn}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: "4px",
                        transform: "translateY(-50%)",
                        minWidth: "28px",
                        width: "28px",
                        height: "28px",
                        color: "fg.muted",
                        zIndex: 1,
                      }}
                      onClick={() => {
                        onReloadMaTn();
                      }}
                    />
                  </Box>
                  {errors.ma_tn && (
                    <FormControl.Validation variant="error">
                      {errors.ma_tn.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="ma_hs"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return true;
                  if (!/^TD\d{6}$/.test(value))
                    return "Mã học sinh phải bắt đầu bằng 'TD' và có đúng 6 chữ số phía sau (VD: TD123456)";
                  return true;
                },
              }}
              render={({ field }) => (
                <>
                  <FormControl>
                    <FormControl.Label>Mã học sinh</FormControl.Label>
                    <Box
                      sx={{
                        position: "relative",
                      }}
                    >
                      <TextInput
                        block
                        {...field}
                        sx={{
                          pr: "36px",
                        }}
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            ma_hs: e.target.value,
                          }));
                          trigger("ma_hs");
                        }}
                      />
                      <IconButton
                        icon={SyncIcon}
                        aria-label="Lấy lại mã học sinh mới nhất"
                        variant="invisible"
                        size="small"
                        disabled={isReloadingMaHs}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: "4px",
                          transform: "translateY(-50%)",
                          minWidth: "28px",
                          width: "28px",
                          height: "28px",
                          color: "fg.muted",
                          zIndex: 1,
                        }}
                        onClick={() => {
                          onReloadMaHs();
                        }}
                      />
                    </Box>
                    {errors.ma_hs && (
                      <FormControl.Validation variant="error">
                        {errors.ma_hs.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                </>
              )}
            />
            <Controller
              name="ma_cu"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormControl.Label>Mã cũ</FormControl.Label>
                  <TextInput
                    block
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        ma_cu: e.target.value,
                      }));
                    }}
                  />
                </FormControl>
              )}
            />

          <FormControl>
            <FormControl.Label>
              Họ tên (Vi) <span style={{ color: "red" }}>*</span>
            </FormControl.Label>
            <TextInput
              block
              name="ho_ten"
              register={register}
              errors={errors}
              required
              validateMessage="Vui lòng điền họ tên tiếng Việt"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCurrentHoTen(e.target.value);
              }}
            />
          </FormControl>

        </Box>
        <Box
          sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(4,1fr)" }}
        >
          <FormControl>
            <FormControl.Label>
              Ngày sinh <span style={{ color: "red" }}>*</span>
            </FormControl.Label>
            <Controller
              name="ngay_sinh"
              control={control}
              rules={{ required: "Vui lòng điền ngày sinh" }}
              render={({ field }) => (
                <>
                  <DateInput
                    value={
                      field.value
                        ? moment(field.value).format("DD/MM/YYYY")
                        : formData?.ngay_sinh
                          ? moment(formData.ngay_sinh as any).format("DD/MM/YYYY")
                          : ""
                    }
                    onValueChanged={(_ddMMyyyy, date) => {
                      field.onChange(date);
                      setFormData((prev) => ({
                        ...prev,
                        ngay_sinh: date as any,
                      }));
                      setCurrentNgaySinh(date);
                    }}
                    width="100%"
                  />
                  {errors.ngay_sinh && (
                    <FormControl.Validation variant="error">
                      {errors.ngay_sinh.message}
                    </FormControl.Validation>
                  )}
                </>
              )}
            />
          </FormControl>

          <Controller
            name="dm_gioitinh_id"
            control={control}
            rules={{ required: "Vui lòng chọn giới tính" }}
            render={({ field }) => (
              <>
                <FormControl>
                  <FormControl.Label>
                    Giới tính <span style={{ color: "red" }}>*</span>
                  </FormControl.Label>
                  <ComboboxGioiTinh
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setFormData((prev) => ({
                        ...prev,
                        dm_gioitinh_id: value,
                      }));
                    }}
                  />
                  {errors.dm_gioitinh_id && (
                    <FormControl.Validation variant="error">
                      {errors.dm_gioitinh_id.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              </>
            )}
          />

          <Controller
            name="dm_quoctich_id"
            control={control}
            rules={{ required: "Vui lòng chọn quốc tịch" }}
            render={({ field }) => (
              <>
                <FormControl>
                  <FormControl.Label>
                    Quốc tịch <span style={{ color: "red" }}>*</span>
                  </FormControl.Label>
                  <ComboboxQuocTich
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setFormData((prev) => ({
                        ...prev,
                        dm_quoctich_id: value,
                      }));
                    }}
                  />
                  {errors.dm_quoctich_id && (
                    <FormControl.Validation variant="error">
                      {errors.dm_quoctich_id.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              </>
            )}
          />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
            <Controller
              name="dm_dantoc_id"
              control={control}
              rules={{ required: "Vui lòng chọn dân tộc" }}
              render={({ field }) => (
                <>
                  <FormControl>
                    <FormControl.Label>
                      Dân tộc <span style={{ color: "red" }}>*</span>
                    </FormControl.Label>
                    <ComboboxDanToc
                      value={field.value}
                      onValueChanged={(value) => {
                        field.onChange(value);
                        setFormData((prev) => ({
                          ...prev,
                          dm_dantoc_id: value,
                        }));
                      }}
                    />
                    {errors.dm_dantoc_id && (
                      <FormControl.Validation variant="error">
                        {errors.dm_dantoc_id.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                </>
              )}
            />

            <Controller
              name="dm_tongiao_id"
              control={control}
              rules={{ required: "Vui lòng chọn tôn giáo" }}
              render={({ field }) => (
                <>
                  <FormControl>
                    <FormControl.Label>
                      Tôn giáo <span style={{ color: "red" }}>*</span>
                    </FormControl.Label>

                    <ComboboxTonGiao
                      value={field.value}
                      onValueChanged={(value) => {
                        field.onChange(value);
                        setFormData((prev) => ({
                          ...prev,
                          dm_tongiao_id: value,
                        }));
                      }}
                    />
                    {errors.dm_tongiao_id && (
                      <FormControl.Validation variant="error">
                        {errors.dm_tongiao_id.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                </>
              )}
            />
          </Box>
        </Box>

        {/* Duplicate check - simple warning */}
        {!isCheckingDuplicate && duplicateStudents.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "border.default",
              backgroundColor: "canvas.subtle",
              fontSize: "13px",
            }}
          >
            <Box sx={{ mb: 2, color: "danger.fg", fontWeight: 600 }}>
              Phát hiện {duplicateStudents.length} học sinh trùng họ tên và ngày sinh:
            </Box>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Mã TN</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Mã HS</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Họ tên</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Ngày sinh</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Năm học</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Hệ</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Khối</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Lớp</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", fontWeight: 600 }}>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {duplicateStudents.map((student) => (
                  <tr key={student.id}>
                    <td style={{ padding: "4px 8px" }}>{student.ma_tn || ""}</td>
                    <td style={{ padding: "4px 8px", fontWeight: 600 }}>{student.ma_hs || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.ho_ten}</td>
                    <td style={{ padding: "4px 8px" }}>{student.ngay_sinh ? moment(student.ngay_sinh).format("DD/MM/YYYY") : ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.nam_hoc || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.ten_he || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.ten_khoi || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.ten_lop || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{student.trang_thai || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(4, 1fr)",
          }}
        >
          <Controller
            name="dm_trangthaihocsinh_id"
            control={control}
            rules={{ required: "Vui lòng chọn trạng thái học sinh" }}
            render={({ field }) => (
              <>
                <FormControl>
                  <FormControl.Label>
                    Trạng thái học sinh <span style={{ color: "red" }}>*</span>
                  </FormControl.Label>
                  <ComboboxTrangThaiHocSinh
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setFormData((prev) => ({
                        ...prev,
                        dm_trangthaihocsinh_id: value,
                      }));
                    }}
                  />
                  {errors.dm_trangthaihocsinh_id && (
                    <FormControl.Validation variant="error">
                      {errors.dm_trangthaihocsinh_id.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              </>
            )}
          />

          <Controller
            name="nam_nhap_hoc"
            control={control}
            rules={{
              required: "Vui lòng chọn năm nhập học",
              validate: (value) => {
                if (!value || !formData.nam_hoc) return true;
                const namHoc = parseInt(formData.nam_hoc?.split(" - ")[0]);
                const namNhapHoc = parseInt(value?.split(" - ")[0]);
                return (
                  namHoc >= namNhapHoc ||
                  "Năm nhập học phải nhỏ hơn hoặc bằng năm học"
                );
              },
            }}
            render={({ field }) => (
              <>
                <FormControl>
                  <FormControl.Label>
                    Năm nhập học <span style={{ color: "red" }}>*</span>
                  </FormControl.Label>
                  <ComboboxNamHoc
                    value={field.value}
                    onValueChanged={(value) => {
                      field.onChange(value);
                      setFormData((prev) => ({
                        ...prev,
                        nam_nhap_hoc: value,
                      }));
                      trigger("nam_nhap_hoc");
                    }}
                  />
                  {errors.nam_nhap_hoc && (
                    <FormControl.Validation variant="error">
                      {errors.nam_nhap_hoc.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              </>
            )}
          />
          <FormControl>
            <FormControl.Label>Ngày nhập học</FormControl.Label>
            <Controller
              name="ngay_nhap_hoc"
              control={control}
              render={({ field }) => (
                <>
                  <DateInput
                    value={
                      field.value
                        ? moment(field.value).format("DD/MM/YYYY")
                        : formData?.ngay_nhap_hoc
                          ? moment(formData.ngay_nhap_hoc as any).format("DD/MM/YYYY")
                          : ""
                    }
                    onValueChanged={(_ddMMyyyy, date) => {
                      field.onChange(date);
                      setFormData((prev) => ({
                        ...prev,
                        ngay_nhap_hoc: date as any,
                      }));
                    }}
                    width="100%"
                  />
                  {errors.ngay_nhap_hoc && (
                    <FormControl.Validation variant="error">
                      {errors.ngay_nhap_hoc.message}
                    </FormControl.Validation>
                  )}
                </>
              )}
            />
          </FormControl>
          <Controller
            name="laconthu"
            control={control}
            render={({ field }) => (
              <>
                <FormControl>
                  <FormControl.Label>Là con thứ</FormControl.Label>
                  <TextInput
                    block
                    type="number"
                    {...field}
                    value={field.value || 1}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === "" || value === "0") {
                        value = "1";
                      }
                      const numericValue = parseInt(value) || 1;
                      field.onChange(numericValue);
                      setFormData((prev) => ({
                        ...prev,
                        laconthu: numericValue,
                      }));
                    }}
                    onBlur={(e) => {
                      if (!e.target.value || e.target.value === "0") {
                        field.onChange(1);
                        setFormData((prev) => ({
                          ...prev,
                          laconthu: 1,
                        }));
                      }
                    }}
                    min="1"
                    placeholder="1"
                  />
                </FormControl>
              </>
            )}
          />
        </Box>
      </Box>
    </FormBlock>
  );
};

export default ThongTinHocSinh;

